// SPDX-License-Identifier: AGPL-3.0-or-later

import {assign, getInitialSnapshot, type SnapshotFrom, setup, transition} from 'xstate';

export type ComposerHolidayPeekMotion = 'idle' | 'pop' | 'hold' | 'morph' | 'hide';

export interface PeekChanceCurve {
	minChars: number;
	maxChars: number;
	pMin: number;
	pMax: number;
}

export type ComposerHolidayPeekCredit = 'sessionChars' | 'charsSinceStage';

export interface ComposerHolidayPeekRule {
	from: string | null | 'ladder';
	to: string;
	activity: 'keystroke';
	credit: ComposerHolidayPeekCredit;
	curve: PeekChanceCurve;
}

export interface ComposerHolidayPeekMachineInput {
	rules: ReadonlyArray<ComposerHolidayPeekRule>;
	ladder: ReadonlyArray<string>;
	hideIdleMs: number;
	hideIdleAfterHoldMs: number;
	holdBeforeLongIdleMs: number;
	respawnCooldownMinMs: number;
	respawnCooldownMaxMs: number;
	maxSpawnsPerCompose: number;
	popMs: number;
	morphMs: number;
	hideMs: number;
	interruptHideIdleMs: number;
	interruptHideIdleAfterHoldMs: number;
	spawnCurveAfterHide: PeekChanceCurve;
	spawnCurveAfterComfort: PeekChanceCurve;
	revealCurveAfterComfort: PeekChanceCurve;
}

export interface ComposerHolidayPeekMachineContext extends ComposerHolidayPeekMachineInput {
	channelId: string | null;
	motion: ComposerHolidayPeekMotion;
	currentStage: string | null;
	nextStage: string | null;
	sessionChars: number;
	charsSinceStage: number;
	respawnChars: number;
	lastChangeAt: number;
	visibleSince: number | null;
	hideAt: number | null;
	motionEndsAt: number | null;
	spawnCount: number;
	lastHiddenAt: number | null;
	respawnReadyAt: number | null;
	interruptStageId: string | null;
	comfortReached: boolean;
}

export type ComposerHolidayPeekMachineEvent =
	| {
			type: 'peek.composerInsert';
			channelId: string;
			now: number;
			roll: number;
			kind: 'keystroke' | 'urlPaste' | 'bulkPaste';
			chars: number;
	  }
	| {
			type: 'peek.composerDelete';
			channelId: string;
			now: number;
	  }
	| {
			type: 'peek.idleElapsed';
			channelId: string;
			now: number;
	  }
	| {
			type: 'peek.motionElapsed';
			channelId: string;
			now: number;
			roll: number;
	  }
	| {
			type: 'peek.composeReset';
			channelId: string;
			now: number;
	  }
	| {
			type: 'peek.interrupt';
			channelId: string;
			now: number;
			stageId: string;
	  }
	| {
			type: 'peek.interruptDismissed';
			channelId: string;
			now: number;
	  }
	| {
			type: 'peek.composerCleared';
			channelId: string;
			now: number;
	  };

export interface ComposerHolidayPeekModel {
	channelId: string | null;
	motion: ComposerHolidayPeekMotion;
	currentStage: string | null;
	nextStage: string | null;
	hideAt: number | null;
	motionEndsAt: number | null;
	sessionChars: number;
	charsSinceStage: number;
}

export type ComposerHolidayPeekView = Pick<
	ComposerHolidayPeekModel,
	'channelId' | 'motion' | 'currentStage' | 'nextStage'
>;

export type ComposerHolidayPeekSnapshot = SnapshotFrom<typeof composerHolidayPeekStateMachine>;

export function peekRollProbability(chars: number, curve: PeekChanceCurve): number {
	if (chars < curve.minChars) return 0;
	if (chars >= curve.maxChars) return curve.pMax;
	const span = curve.maxChars - curve.minChars;
	if (span <= 0) return curve.pMax;
	const t = (chars - curve.minChars) / span;
	const eased = t * t * t;
	return curve.pMin + (curve.pMax - curve.pMin) * eased;
}

function initialContext(input: ComposerHolidayPeekMachineInput): ComposerHolidayPeekMachineContext {
	return {
		...input,
		channelId: null,
		motion: 'idle',
		currentStage: null,
		nextStage: null,
		sessionChars: 0,
		charsSinceStage: 0,
		respawnChars: 0,
		lastChangeAt: 0,
		visibleSince: null,
		hideAt: null,
		motionEndsAt: null,
		spawnCount: 0,
		lastHiddenAt: null,
		respawnReadyAt: null,
		interruptStageId: null,
		comfortReached: false,
	};
}

function resetSession(
	context: ComposerHolidayPeekMachineContext,
	channelId: string | null,
): ComposerHolidayPeekMachineContext {
	return {
		...context,
		channelId,
		motion: 'idle',
		currentStage: null,
		nextStage: null,
		sessionChars: 0,
		charsSinceStage: 0,
		respawnChars: 0,
		lastChangeAt: 0,
		visibleSince: null,
		hideAt: null,
		motionEndsAt: null,
		spawnCount: 0,
		lastHiddenAt: null,
		respawnReadyAt: null,
		interruptStageId: null,
		comfortReached: false,
	};
}

function isVisibleMotion(motion: ComposerHolidayPeekMotion): boolean {
	return motion === 'pop' || motion === 'hold' || motion === 'morph';
}

function isLastLadderStage(ladder: ReadonlyArray<string>, stageId: string | null): boolean {
	if (stageId == null || ladder.length === 0) return false;
	return ladder[ladder.length - 1] === stageId;
}

function idleDelayMs(context: ComposerHolidayPeekMachineContext, now: number): number {
	const interrupt = context.interruptStageId != null;
	if (interrupt) {
		if (context.visibleSince != null && now - context.visibleSince >= context.holdBeforeLongIdleMs) {
			return context.interruptHideIdleAfterHoldMs;
		}
		return context.interruptHideIdleMs;
	}
	if (isLastLadderStage(context.ladder, context.currentStage)) {
		return context.hideIdleAfterHoldMs;
	}
	if (context.visibleSince != null && now - context.visibleSince >= context.holdBeforeLongIdleMs) {
		return context.hideIdleAfterHoldMs;
	}
	return context.hideIdleMs;
}

function hideAtAfterActivity(
	context: ComposerHolidayPeekMachineContext,
	now: number,
	motion: ComposerHolidayPeekMotion,
): number | null {
	if (!isVisibleMotion(motion)) return null;
	return now + idleDelayMs(context, now);
}

function insertCredit(kind: 'keystroke' | 'urlPaste' | 'bulkPaste', chars: number): number {
	if (kind === 'keystroke' || kind === 'urlPaste') return 1;
	return Math.max(0, chars);
}

function canSpawn(context: ComposerHolidayPeekMachineContext, now: number): boolean {
	if (context.spawnCount >= context.maxSpawnsPerCompose) return false;
	if (context.respawnReadyAt != null && now < context.respawnReadyAt) return false;
	return true;
}

function respawnCooldownMs(context: ComposerHolidayPeekMachineContext, roll: number): number {
	const t = Math.min(1, Math.max(0, roll));
	return context.respawnCooldownMinMs + (context.respawnCooldownMaxMs - context.respawnCooldownMinMs) * t;
}

function ruleApplies(
	rule: ComposerHolidayPeekRule,
	currentStage: string | null,
	ladder: ReadonlyArray<string>,
): boolean {
	if (rule.from === null) return currentStage == null;
	if (rule.from === 'ladder') {
		if (currentStage == null) return false;
		const index = ladder.indexOf(currentStage);
		return index >= 0 && index < ladder.length - 1 && rule.to === ladder[index + 1];
	}
	return rule.from === currentStage;
}

function curveForRule(context: ComposerHolidayPeekMachineContext, rule: ComposerHolidayPeekRule): PeekChanceCurve {
	if (rule.from === null) {
		if (context.comfortReached) return context.spawnCurveAfterComfort;
		if (context.lastHiddenAt != null) return context.spawnCurveAfterHide;
		return rule.curve;
	}
	if (context.comfortReached) return context.revealCurveAfterComfort;
	return rule.curve;
}

function creditForRule(
	context: ComposerHolidayPeekMachineContext,
	rule: ComposerHolidayPeekRule,
	sessionChars: number,
	charsSinceStage: number,
	respawnChars: number,
): number {
	if (rule.from === null) {
		return context.lastHiddenAt != null || context.spawnCount > 0 ? respawnChars : sessionChars;
	}
	return charsSinceStage;
}

function findHitRule(
	context: ComposerHolidayPeekMachineContext,
	currentStage: string | null,
	sessionChars: number,
	charsSinceStage: number,
	respawnChars: number,
	roll: number,
): ComposerHolidayPeekRule | null {
	for (const rule of context.rules) {
		if (rule.activity !== 'keystroke') continue;
		if (!ruleApplies(rule, currentStage, context.ladder)) continue;
		const probability = peekRollProbability(
			creditForRule(context, rule, sessionChars, charsSinceStage, respawnChars),
			curveForRule(context, rule),
		);
		if (probability > 0 && roll < probability) return rule;
	}
	return null;
}

function beginHold(
	context: ComposerHolidayPeekMachineContext,
	now: number,
	currentStage: string | null,
	charsSinceStage: number,
): Partial<ComposerHolidayPeekMachineContext> {
	const held: ComposerHolidayPeekMachineContext = {
		...context,
		motion: 'hold',
		currentStage,
		nextStage: null,
		charsSinceStage,
		motionEndsAt: null,
	};
	return {
		motion: 'hold',
		currentStage,
		nextStage: null,
		charsSinceStage,
		motionEndsAt: null,
		hideAt: now + idleDelayMs(held, now),
		comfortReached: context.comfortReached || isLastLadderStage(context.ladder, currentStage),
	};
}

function beginHide(
	context: ComposerHolidayPeekMachineContext,
	now: number,
): Partial<ComposerHolidayPeekMachineContext> {
	if (!isVisibleMotion(context.motion)) return {};
	return {
		motion: 'hide',
		hideAt: null,
		motionEndsAt: now + context.hideMs,
	};
}

function alreadyShowingStage(context: ComposerHolidayPeekMachineContext, stageId: string): boolean {
	return context.currentStage === stageId || context.nextStage === stageId;
}

function interruptToStage(
	context: ComposerHolidayPeekMachineContext,
	channelId: string,
	now: number,
	stageId: string,
): Partial<ComposerHolidayPeekMachineContext> {
	if (alreadyShowingStage(context, stageId)) return {};
	if (isVisibleMotion(context.motion)) {
		const morphing: ComposerHolidayPeekMachineContext = {
			...context,
			motion: 'morph',
			nextStage: stageId,
			interruptStageId: stageId,
		};
		return {
			channelId,
			lastChangeAt: now,
			motion: 'morph',
			nextStage: stageId,
			charsSinceStage: 0,
			motionEndsAt: now + context.morphMs,
			hideAt: hideAtAfterActivity(morphing, now, 'morph'),
			interruptStageId: stageId,
		};
	}
	return {
		channelId,
		lastChangeAt: now,
		motion: 'pop',
		currentStage: stageId,
		nextStage: null,
		charsSinceStage: 0,
		visibleSince: now,
		motionEndsAt: now + context.popMs,
		hideAt: now + context.interruptHideIdleMs,
		interruptStageId: stageId,
	};
}

function dismissInterrupt(
	context: ComposerHolidayPeekMachineContext,
	now: number,
): Partial<ComposerHolidayPeekMachineContext> {
	if (context.interruptStageId == null) return {};
	return beginHide(context, now);
}

function clearComposer(
	context: ComposerHolidayPeekMachineContext,
	channelId: string,
	now: number,
): Partial<ComposerHolidayPeekMachineContext> {
	if (isVisibleMotion(context.motion)) {
		return {
			...beginHide(context, now),
			channelId,
			lastChangeAt: now,
			sessionChars: 0,
			charsSinceStage: 0,
			respawnChars: 0,
			spawnCount: 0,
			respawnReadyAt: null,
			comfortReached: false,
		};
	}
	if (context.motion === 'hide') {
		return {
			channelId,
			lastChangeAt: now,
			sessionChars: 0,
			charsSinceStage: 0,
			respawnChars: 0,
			spawnCount: 0,
			respawnReadyAt: null,
			comfortReached: false,
		};
	}
	return resetSession(context, channelId);
}

export const composerHolidayPeekStateMachine = setup({
	types: {} as {
		context: ComposerHolidayPeekMachineContext;
		events: ComposerHolidayPeekMachineEvent;
		input: ComposerHolidayPeekMachineInput;
	},
	actions: {
		onComposerInsert: assign(({context, event}) => {
			if (event.type !== 'peek.composerInsert') return {};
			if (context.channelId != null && context.channelId !== event.channelId) return {};
			const credit = insertCredit(event.kind, event.chars);
			const sessionChars = context.sessionChars + credit;
			const charsSinceStage = isVisibleMotion(context.motion)
				? context.charsSinceStage + credit
				: context.charsSinceStage;
			const respawnChars = context.motion === 'idle' ? context.respawnChars + credit : context.respawnChars;
			const lastChangeAt = event.now;
			const hideAt = hideAtAfterActivity(context, event.now, context.motion);
			if (event.kind !== 'keystroke') {
				return {channelId: event.channelId, sessionChars, charsSinceStage, respawnChars, lastChangeAt, hideAt};
			}
			if (context.motion === 'idle') {
				if (!canSpawn(context, event.now)) {
					return {channelId: event.channelId, sessionChars, charsSinceStage, respawnChars, lastChangeAt, hideAt};
				}
				const hit = findHitRule(
					{...context, sessionChars, charsSinceStage, respawnChars},
					null,
					sessionChars,
					charsSinceStage,
					respawnChars,
					event.roll,
				);
				if (hit == null) {
					return {channelId: event.channelId, sessionChars, charsSinceStage, respawnChars, lastChangeAt, hideAt};
				}
				return {
					channelId: event.channelId,
					sessionChars,
					charsSinceStage: 0,
					respawnChars: 0,
					lastChangeAt,
					motion: 'pop',
					currentStage: hit.to,
					nextStage: null,
					visibleSince: event.now,
					motionEndsAt: event.now + context.popMs,
					hideAt: event.now + context.hideIdleMs,
					spawnCount: context.spawnCount + 1,
					respawnReadyAt: null,
				};
			}
			if (context.motion === 'hold') {
				const hit = findHitRule(
					{...context, sessionChars, charsSinceStage, respawnChars},
					context.currentStage,
					sessionChars,
					charsSinceStage,
					respawnChars,
					event.roll,
				);
				if (hit == null) {
					return {channelId: event.channelId, sessionChars, charsSinceStage, respawnChars, lastChangeAt, hideAt};
				}
				return {
					channelId: event.channelId,
					sessionChars,
					charsSinceStage,
					respawnChars,
					lastChangeAt,
					motion: 'morph',
					nextStage: hit.to,
					motionEndsAt: event.now + context.morphMs,
					hideAt: hideAtAfterActivity({...context, motion: 'morph'}, event.now, 'morph'),
				};
			}
			return {channelId: event.channelId, sessionChars, charsSinceStage, respawnChars, lastChangeAt, hideAt};
		}),
		onComposerDelete: assign(({context, event}) => {
			if (event.type !== 'peek.composerDelete') return {};
			if (context.channelId != null && context.channelId !== event.channelId) return {};
			return {
				channelId: event.channelId,
				lastChangeAt: event.now,
				hideAt: hideAtAfterActivity(context, event.now, context.motion),
			};
		}),
		onIdleElapsed: assign(({context, event}) => {
			if (event.type !== 'peek.idleElapsed') return {};
			if (context.channelId !== event.channelId) return {};
			if (context.motion !== 'hold') return {};
			if (context.hideAt == null || event.now < context.hideAt) return {};
			return beginHide(context, event.now);
		}),
		onMotionElapsed: assign(({context, event}) => {
			if (event.type !== 'peek.motionElapsed') return {};
			if (context.channelId !== event.channelId) return {};
			if (context.motionEndsAt == null || event.now < context.motionEndsAt) return {};
			if (context.motion === 'pop') {
				return beginHold(context, event.now, context.currentStage, context.charsSinceStage);
			}
			if (context.motion === 'morph' && context.nextStage != null) {
				return beginHold(context, event.now, context.nextStage, 0);
			}
			if (context.motion === 'hide') {
				return {
					motion: 'idle',
					currentStage: null,
					nextStage: null,
					charsSinceStage: 0,
					respawnChars: 0,
					visibleSince: null,
					hideAt: null,
					motionEndsAt: null,
					lastHiddenAt: event.now,
					sessionChars: context.sessionChars,
					interruptStageId: null,
					respawnReadyAt: event.now + respawnCooldownMs(context, event.roll),
				};
			}
			return {};
		}),
		onComposeReset: assign(({context, event}) => {
			if (event.type !== 'peek.composeReset') return {};
			return resetSession(context, event.channelId);
		}),
		onComposerCleared: assign(({context, event}) => {
			if (event.type !== 'peek.composerCleared') return {};
			if (context.channelId != null && context.channelId !== event.channelId) return {};
			return clearComposer(context, event.channelId, event.now);
		}),
		onInterrupt: assign(({context, event}) => {
			if (event.type !== 'peek.interrupt') return {};
			if (context.channelId != null && context.channelId !== event.channelId) return {};
			return interruptToStage(context, event.channelId, event.now, event.stageId);
		}),
		onInterruptDismissed: assign(({context, event}) => {
			if (event.type !== 'peek.interruptDismissed') return {};
			if (context.channelId != null && context.channelId !== event.channelId) return {};
			return dismissInterrupt(context, event.now);
		}),
	},
}).createMachine({
	id: 'composerHolidayPeek',
	context: ({input}) => initialContext(input),
	initial: 'ready',
	states: {
		ready: {
			on: {
				'peek.composerInsert': {actions: 'onComposerInsert'},
				'peek.composerDelete': {actions: 'onComposerDelete'},
				'peek.idleElapsed': {actions: 'onIdleElapsed'},
				'peek.motionElapsed': {actions: 'onMotionElapsed'},
				'peek.composeReset': {actions: 'onComposeReset'},
				'peek.composerCleared': {actions: 'onComposerCleared'},
				'peek.interrupt': {actions: 'onInterrupt'},
				'peek.interruptDismissed': {actions: 'onInterruptDismissed'},
			},
		},
	},
});

export function createComposerHolidayPeekSnapshot(input: ComposerHolidayPeekMachineInput): ComposerHolidayPeekSnapshot {
	return getInitialSnapshot(composerHolidayPeekStateMachine, input);
}

export function transitionComposerHolidayPeekSnapshot(
	snapshot: ComposerHolidayPeekSnapshot,
	event: ComposerHolidayPeekMachineEvent,
): ComposerHolidayPeekSnapshot {
	return transition(composerHolidayPeekStateMachine, snapshot, event)[0] as ComposerHolidayPeekSnapshot;
}

export function selectComposerHolidayPeekModel(snapshot: ComposerHolidayPeekSnapshot): ComposerHolidayPeekModel {
	return {
		channelId: snapshot.context.channelId,
		motion: snapshot.context.motion,
		currentStage: snapshot.context.currentStage,
		nextStage: snapshot.context.nextStage,
		hideAt: snapshot.context.hideAt,
		motionEndsAt: snapshot.context.motionEndsAt,
		sessionChars: snapshot.context.sessionChars,
		charsSinceStage: snapshot.context.charsSinceStage,
	};
}

export function selectComposerHolidayPeekView(snapshot: ComposerHolidayPeekSnapshot): ComposerHolidayPeekView | null {
	if (snapshot.context.motion === 'idle') return null;
	return {
		channelId: snapshot.context.channelId,
		motion: snapshot.context.motion,
		currentStage: snapshot.context.currentStage,
		nextStage: snapshot.context.nextStage,
	};
}

export function composerHolidayPeekViewEquals(
	left: ComposerHolidayPeekView | null,
	right: ComposerHolidayPeekView | null,
): boolean {
	if (left == null || right == null) return left === right;
	return (
		left.channelId === right.channelId &&
		left.motion === right.motion &&
		left.currentStage === right.currentStage &&
		left.nextStage === right.nextStage
	);
}
